/**
 * @fileoverview Main application store using Zustand
 * This file creates and exports the main store with all slices combined.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { createBacktestSlice } from './slices/backtestSlice';
import { createUserSlice } from './slices/userSlice';
import type { BacktestSlice } from '../types/store';
import type { UserSlice } from '../types/user';

// Main store type combining all slices
export type AppStore = BacktestSlice & UserSlice;

// Create the main store with all slices
export const useStore = create<AppStore>()(
  devtools(
    immer((...args) => ({
      ...createBacktestSlice(...args),
      ...createUserSlice(...args),
    })),
    {
      name: 'quanthop-app-store',
      enabled: import.meta.env.DEV,
    }
  )
);

// Selector hooks for better performance and type safety
export const useBacktestStore = () => useStore();
export const useUserStore = () => useStore();

// Additional selector hooks for specific state slices
export const useBacktestResults = () => useStore((state) => ({
  previewResult: state.previewResult,
  optimizationResults: state.optimizationResults,
  previewState: state.previewState,
  optimizationState: state.optimizationState,
}));

export const useBacktestForm = () => useStore((state) => ({
  formData: state.formData,
  backtestName: state.backtestName,
  setFormData: state.setFormData,
  setBacktestName: state.setBacktestName,
}));

export const useBacktestAsync = () => useStore((state) => ({
  previewState: state.previewState,
  optimizationState: state.optimizationState,
  saveState: state.saveState,
  setPreviewState: state.setPreviewState,
  setOptimizationState: state.setOptimizationState,
  setSaveState: state.setSaveState,
}));
